import { BreakpointObserver } from "@angular/cdk/layout";
import {
  AfterViewInit,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from "@angular/router";
import { BELocalizationService } from "@core/services/global/localization/be-localization.service";
import { TextApp } from "@core/services/global/localization/interfaces/localization.interfaces";
import { HeaderScrollService } from "@core/services/utils/header-scroll.service";
import { TranslateService } from "@ngx-translate/core";
import { BEBasicService } from "@services/be-basic.service";
import { ContactSidebarComponent } from "@ui/layout/contact-sidebar/contact-sidebar.component";
import { ExtraInfoHeaderComponent } from "@ui/layout/extra-info-header/extra-info-header.component";
import { MobileNavComponent } from "@ui/layout/mobile-nav/mobile-nav.component";
import { Observable, of, Subject } from "rxjs";
import { filter, map, takeUntil } from "rxjs/operators";

import { processHttpError } from "@core/services/utils/process-http-error";
import { CookieConsentService } from "./core/services/utils/cookie-consent.service";
import { UserStorageService } from "./core/services/utils/user-storage.service";
import { BeUsersService } from "./services/business/user-services/be-users.service";
import { MetaTagService } from "./services/meta-tag.service";
import { BENotificationsService } from "./services/ui-helpers/notifications-services/be-notifications.service";
import { LcMenuComponent } from "./ui/layout/lc-menu/lc-menu.component";
import { ResponsiveHeaderComponent } from "./ui/layout/responsive-header/responsive-header.component";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
  imports: [
    LcMenuComponent,
    RouterOutlet,
    ResponsiveHeaderComponent,
    ExtraInfoHeaderComponent,
    ContactSidebarComponent,
    MobileNavComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly apiServices = inject(BEBasicService);
  private readonly userStorageService = inject(UserStorageService);
  private readonly translate = inject(TranslateService);
  private readonly localizationService = inject(BELocalizationService);
  private readonly userService = inject(BeUsersService);
  private readonly notificationsService = inject(BENotificationsService);
  private readonly usersService = inject(BeUsersService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly headerScrollService = inject(HeaderScrollService);
  private readonly metaTagService = inject(MetaTagService);
  private readonly cookieConsentService = inject(CookieConsentService);

  public isLoggedIn: boolean;
  public isSmallScreen: boolean;

  private readonly destroy$ = new Subject<void>();
  protected isHeaderScrolled = false;
  private readonly DEFAULT_LOCALE: string = "es";

  private static isAppReady = false;

  protected readonly isMobile = toSignal(
    this.breakpointObserver
      .observe(["(max-width: 48rem)"])
      .pipe(map(result => result.matches)),
    { initialValue: false }
  );

  private readonly isTablet = toSignal(
    this.breakpointObserver
      .observe(["(max-width: 48rem)", "(max-width: 64rem)"])
      .pipe(
        map(
          result =>
            result.breakpoints["(max-width: 64rem)"] &&
            !result.breakpoints["(max-width: 48rem)"]
        )
      ),
    { initialValue: false }
  );

  protected isClientUser = false;

  public showDesktopMenuInRoute = true;
  public showMobileHeaderInRoute = true;
  public showExtraMenuHeaderInRoute = true;
  public showContactSidebarInRoute = true;
  public showMobileMenuInRoute = true;

  public customMobileLayout = false;

  constructor() {
    this.isSmallScreen = false;
    this.isLoggedIn = false;
    let locale: string = this.localizationService.getStoredLocaleDirect();

    if (!locale || locale.length < 1) {
      this.localizationService.setLocale(this.DEFAULT_LOCALE);
      locale = this.DEFAULT_LOCALE;
    }

    this.translate.setDefaultLang(locale);
    this.localizationService
      .initTextEngine()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: TextApp) => {
          if (data?.data && data?.langId?.length > 0) {
            this.translate.setTranslation(locale, data.data);
            document.title = this.translate.instant("App.NameAdmin");

            this.setupMenuVisibility();
            AppComponent.isAppReady = true;
          } else {
            AppComponent.isAppReady = false;
          }
        },
        error: err => {
          processHttpError(err);
          AppComponent.isAppReady = false;
        },
      });
  }

  private setupMenuVisibility() {
    // Set initial state based on current route
    this.updateMenuVisibilityFromRoute();

    // Subscribe to navigation events for future route changes
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateMenuVisibilityFromRoute();
      });
  }

  private updateMenuVisibilityFromRoute() {
    let route = this.activatedRoute.firstChild;
    while (route?.firstChild) {
      route = route.firstChild;
    }
    this.showDesktopMenuInRoute =
      route?.snapshot?.data?.["showDesktopMenu"] !== false;
    this.showMobileHeaderInRoute =
      route?.snapshot?.data?.["showMobileHeader"] !== false;
    this.customMobileLayout =
      route?.snapshot?.data?.["customMobileLayout"] !== false;
    this.showExtraMenuHeaderInRoute =
      route?.snapshot?.data?.["showExtraMenuHeader"] !== false;
    this.showContactSidebarInRoute =
      route?.snapshot?.data?.["showContactSidebar"] !== false;
    this.showMobileMenuInRoute =
      route?.snapshot?.data?.["showMobileMenu"] !== false;

    // Update meta tags based on route data
    this.updateMetaTagsFromRoute(route);
  }

  private updateMetaTagsFromRoute(route: ActivatedRoute | null) {
    const metaConfig = route?.snapshot?.data?.["meta"];
    if (metaConfig) {
      this.metaTagService.updateMetaTags(metaConfig);
    } else {
      // Set default meta tags if no specific configuration is found
      this.metaTagService.setDefaultMetaTags();
    }
  }

  public static appReady(): boolean {
    return AppComponent.isAppReady;
  }

  public static apprReadyObservable(): Observable<boolean> {
    return of(AppComponent.isAppReady);
  }

  public logIn() {
    this.apiServices
      .isUserLoggedIn()
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.isLoggedIn = data;

        if (this.isLoggedIn) {
          this.notificationsService.startSse();
          this.userStorageService
            .getRegisteredUser()
            .pipe(takeUntil(this.destroy$))
            .subscribe();

          this.userService
            .loadRoles()
            .then(() => {
              this.isClientUser = this.usersService.isOnlyClient();
            })
            .catch(err => {
              processHttpError(err);
            });
        }
      });
  }

  ngOnInit(): void {
    // Subscribe to header scroll state
    this.headerScrollService.isScrolled$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isScrolled => {
        this.isHeaderScrolled = isScrolled;
      });

    this.logIn();
  }

  ngAfterViewInit(): void {
    this.cookieConsentService.init();
  }

  protected get showExtraInfoHeader(): boolean {
    if (!this.isLoggedIn) {
      return false;
    }

    return (
      this.showExtraMenuHeaderInRoute && !this.isMobile() && this.isClientUser
    );
  }

  protected get showContactSidebar(): boolean {
    if (!this.isLoggedIn) {
      return false;
    }

    return (
      this.showContactSidebarInRoute &&
      !this.isMobile() &&
      !this.isTablet() &&
      this.isClientUser
    );
  }

  protected get showDesktopMenu(): boolean {
    return !this.isMobile() && this.isLoggedIn && this.showDesktopMenuInRoute;
  }

  protected get showMobileMenu(): boolean {
    return (
      this.showMobileMenuInRoute &&
      this.isMobile() &&
      this.isLoggedIn &&
      this.isClientUser
    );
  }

  protected get showHeader(): boolean {
    return (
      !this.isMobile() || (this.isMobile() && this.showMobileHeaderInRoute)
    );
  }

  protected readonly isCustomMobileLayout = computed(
    () => this.isMobile() && this.customMobileLayout
  );

  protected get isAppReady(): boolean {
    return AppComponent.isAppReady;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
